import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import styled from "styled-components";
import UserFolderLayout from "../UserFolderLayout";
import { Render, fetchRenders, hasProcessingRenders } from "../services/renders-api";

/**
 * User Folder Page
 * 
 * Displays rendered images for a user folder.
 * Reads the userFolderId from the `user` query parameter.
 * 
 * URL: /folder?user=<userFolderId>
 */
const UserFolderPage = () => {
  const [searchParams] = useSearchParams();
  const userFolderId = searchParams.get('user');

  const [renders, setRenders] = useState<Render[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  /**
   * Fetch all renders from the API across all pages.
   * Fetches page 1 first to get total/pageSize,
   * then fetches all remaining pages in parallel and merges results.
   */
  const loadRenders = useCallback(async () => {
    if (!userFolderId) return;

    try {
      // Fetch first page to get pagination metadata
      const firstPage = await fetchRenders(userFolderId, 1);
      const { total, pageSize, items: firstItems } = firstPage;
      const totalPages = Math.ceil(total / pageSize);

      if (totalPages <= 1) {
        setRenders(firstItems);
      } else {
        // Fetch all remaining pages in parallel
        const remainingPages = await Promise.all(
          Array.from({ length: totalPages - 1 }, (_, i) =>
            fetchRenders(userFolderId, i + 2)
          )
        );
        setRenders([
          ...firstItems,
          ...remainingPages.flatMap(p => p.items),
        ]);
      }

      setError(null);
    } catch (err) {
      console.error('Failed to fetch renders:', err);
      setError('Impossible de charger les rendus. Veuillez recharger la page pour les recharger.');
    } finally {
      setLoading(false);
    }
  }, [userFolderId]);

  // Initial load
  useEffect(() => {
    if (!userFolderId) {
      setLoading(false);
      setError('Identifiant utilisateur manquant');
      return;
    }

    loadRenders();
  }, [userFolderId, loadRenders]);

  // Auto-refresh while renders are processing
  useEffect(() => {
    if (loading || error || !userFolderId) return;

    // Check if there are processing renders
    // if (!hasProcessingRenders(renders)) return;

    // Poll every 5 seconds while renders are processing
    const intervalId = setInterval(() => {
      loadRenders();
    }, 5000);

    return () => clearInterval(intervalId);
  }, [renders, loading, error, userFolderId, loadRenders]);

  // Show error page if no user ID
  if (!userFolderId && !loading) {
    return (
      <ErrorContainer>
        <ErrorContent>
          <ErrorTitle>Identifiant manquant</ErrorTitle>
          <ErrorMessage>
            Veuillez fournir un identifiant utilisateur valide dans l'URL.
          </ErrorMessage>
          <ErrorExample>
            Exemple: /folder?user=votre-identifiant
          </ErrorExample>
        </ErrorContent>
      </ErrorContainer>
    );
  }

  return (
    <PageContainer>
      {hasProcessingRenders(renders) && (
        <TopBar>
          <RefreshIndicator>
            <RefreshSpinner />
            Mise à jour en cours...
          </RefreshIndicator>
        </TopBar>
      )}
      <UserFolderLayout
        renders={renders}
        loading={loading}
        error={error}
      />
    </PageContainer>
  );
};

// Styled components
const PageContainer = styled.div`
  height: 100vh;
  display: flex;
  flex-direction: column;
  position: relative;
`;

const TopBar = styled.div`
  position: absolute;
  top: 1rem;
  right: 1rem;
  z-index: 1000;
`;

const RefreshIndicator = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  background: rgba(255, 255, 255, 0.9);
  border-radius: 8px;
  font-size: 0.85rem;
  color: #666;
  font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
`;

const RefreshSpinner = styled.div`
  width: 16px;
  height: 16px;
  border: 2px solid #ddd;
  border-top-color: #006ab2;
  border-radius: 50%;
  animation: spin 1s linear infinite;

  @keyframes spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }
`;

const ErrorContainer = styled.div`
  height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #667eea 0%, #4b61a2ff 100%);
  padding: 2rem;
`;

const ErrorContent = styled.div`
  background: white;
  padding: 3rem;
  border-radius: 16px;
  text-align: center;
  max-width: 400px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.2);
`;

const ErrorTitle = styled.h1`
  color: #333;
  font-size: 1.5rem;
  margin: 0 0 1rem 0;
  font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
`;

const ErrorMessage = styled.p`
  color: #666;
  font-size: 1rem;
  margin: 0 0 1rem 0;
  font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
`;

const ErrorExample = styled.code`
  display: block;
  background: #f5f5f5;
  padding: 0.75rem;
  border-radius: 8px;
  font-size: 0.85rem;
  color: #666;
  margin-bottom: 1.5rem;
`;

export default UserFolderPage;
